/**
 * Ref-master API service.
 *
 * Endpoints:
 *   Single : GET /api/ref?category=<category>
 *   Batch  : GET /api/ref/batch?categories=<cat1>,<cat2>,...
 *
 * Both return { success: boolean; data: { category: string; options: RefOption[] } }
 * The batch endpoint returns { success: boolean; data: Record<string, RefOption[]> }
 */

import apiClient from './apiClient'
import { REF_SINGLE, REF_BATCH, usageSubtypes } from './apiEndpoints'
import { log } from '@/utils/logger'

type ServiceError = { response?: { data: unknown }; request?: unknown; message?: string }

export interface RefOption {
  id: number
  code: string
  label: string
}

interface SingleRefResponse {
  success: boolean
  data: {
    category: string
    options: RefOption[]
  }
}

interface BatchRefResponse {
  success: boolean
  data: Record<string, RefOption[]>
}

export async function fetchRefSingle(category: string): Promise<RefOption[]> {
  try {
    const response = await apiClient.get<SingleRefResponse>(REF_SINGLE, { params: { category } })
    const options = response.data?.data?.options ?? []
    log.info('fetchRefSingle success', { category, count: String(options.length) })
    return options
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.error('fetchRefSingle failed — server error', new Error('API error'), { category })
      return err.response.data as RefOption[]
    } else {
      log.error('fetchRefSingle failed — unexpected', new Error(err.message ?? 'Unknown'), { category })
      throw new Error(err.message || 'Unexpected error occurred')
    }
  }
}

interface UsageSubtypesResponse {
  success: boolean
  data: {
    category: string
    options: RefOption[]
  }
}

/**
 * Live lookup of Subtype values for a Truck Use Type / Trailer Type, via
 * GET /api/vvs-master/usage-subtypes/:vehicleType/:label. On the backend this
 * calls VVS on first lookup for the label and caches the result (including a
 * "no subtypes" outcome) in ref_master — subsequent calls are served from there.
 * Returns { category, options } so the caller can seed the ref cache under the
 * exact category the options were stored against.
 */
export async function fetchUsageSubtypes(
  vehicleType: 'TT' | 'TR',
  label: string
): Promise<{ category: string; options: RefOption[] }> {
  try {
    const response = await apiClient.get<UsageSubtypesResponse>(usageSubtypes(vehicleType, label))
    const data = response.data?.data ?? { category: '', options: [] }
    log.info('fetchUsageSubtypes success', { vehicleType, label, count: String(data.options.length) })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    const message = err.message ?? 'Unexpected error occurred'
    log.error('fetchUsageSubtypes failed', new Error(message), { vehicleType, label })
    throw new Error(message)
  }
}

export async function fetchRefBatch(
  categories: string[]
): Promise<Record<string, RefOption[]>> {
  if (categories.length === 0) return {}
  log.info('fetchRefBatch', { categoryCount: String(categories.length) })
  try {
    const response = await apiClient.get<BatchRefResponse>(REF_BATCH, {
      params: { categories: categories.join(',') },
    })
    const result = response.data?.data ?? {}
    log.info('fetchRefBatch success', { categoryCount: String(Object.keys(result).length) })
    return result
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.error('fetchRefBatch failed — server error', new Error('API error'), { categoryCount: String(categories.length) })
      return err.response.data as Record<string, RefOption[]>
    } else {
      log.error('fetchRefBatch failed — unexpected', new Error(err.message ?? 'Unknown'), { categoryCount: String(categories.length) })
      throw new Error(err.message || 'Unexpected error occurred')
    }
  }
}
